import {Injectable} from '@angular/core';
import {Network, WiFiConnectionStatus} from "../../model/networks";
import {Events, Platform} from "ionic-angular";
import {Box, EMGConfig, Log, ScanTypes, SpinalLocation} from "../../model/weak-entities";
import {ClaEvents} from "../../model/events";
import {CalibrationCompleteResults, ScanSettings, ThermalSettings} from "../../model/scanSettings";
import {delay} from "../../utils/utils";
import {HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel} from "@microsoft/signalr";
import {ScanViewUtils} from "../../components/scan-view/scan-view";

const WEB_URL = "insightsynapse.com";

@Injectable()
export class ApiBoxProvider {

  private connection: HubConnection;
  private onBoardConnection: HubConnection;
  private _scanningState: ScanSettings = null;
  private boxDeviceId: string;
  private box: Box;
  private disableDisconnectEvent: boolean = false;
  public offlineMode: boolean = false;
  private iOS11Available = false;
  public isReconnecting: boolean = false;
  private onBoardConnectAttempt: number = 0;
  private connectAttempt: number = 0;
  public get currentVersion(): string {
    if(!this.box) {
      return '';
    }

    return this.box.CurrentVersion;
  }
  public get emgMode(): number {
    if(!this.box) {
      return 0;
    }

    return this.box.EmgMode;
  }
  public get emgNotchFreq(): number {
    if(!this.box) {
      return 1;
    }

    return this.box.EmgNotchFreq;
  }
  public get scanningState(): ScanSettings {
    return this._scanningState;
  }
  constructor(public events: Events,
              public platform: Platform,
              ) {

    if (document.URL.startsWith('ionic')) {
      this.iOS11Available = true;
    }
  }

  public versionCheck(version: string): boolean {
    if(version.length < 1) return true;
    if(this.currentVersion.length < 1) return false;

    let versionOctets = version.split(".");
    let octets = this.currentVersion.split(".");
    if(parseInt(versionOctets[1]) > parseInt(octets[1])) return false;
    if(parseInt(versionOctets[2]) > parseInt(octets[2]) && (parseInt(versionOctets[1]) == parseInt(octets[1])) ) return false;
    return true;
  }

  //region CONNECTIONS

  public async disconnectFromBox(silent: boolean = false) {
    if (this.connection) {
      this.disableDisconnectEvent = silent;
      await this.connection.stop().catch((err) => {

      });
      this.connection = undefined;
      this._scanningState = undefined;
    }
  }

  // Use this for all non-onboarding connections
  public async connectToBox(box: Box) {
    this.connectAttempt = 0;
    this.box = box;
    // Always break if we have a connection
    if (this.connection != undefined) {
      this.disableDisconnectEvent = true;
      // Stop it, otherwise the old socket stays open alongside the new one
      const old = this.connection;
      this.connection = undefined;
      await old.stop().catch(() => {});
    }

    let url = `https://${box.DeviceId}.${WEB_URL}:5002/signalr`;
    if(this.offlineMode || box.SslFallback || this.iOS11Available) {
      url = `http://${box.IpAddress}:5001/signalr`;
    }
    this.connection = new HubConnectionBuilder()
      .withUrl(url, {transport: HttpTransportType.WebSockets, skipNegotiation: true})
      .withAutomaticReconnect([0, 2000, 2000, 2000, 4000, 4000, 30000])
      .configureLogging(LogLevel.Information)
      .build();

    this.connection.on('CalibrationResult', (result: boolean) => {this.events.publish(ClaEvents.CALIBRATION_RESULT, result);});
    this.connection.on('CalibrationCompleteResult', (result: CalibrationCompleteResults) => {this.events.publish(ClaEvents.CALIBRATIONCOMPLETE_RESULT, result);});
    this.connection.on('CalibrationMessage', (msg: string) => {this.events.publish(ClaEvents.CALIBRATION_MESSAGE, msg);});
    this.connection.on('ScanData',(scanType: ScanTypes,data:any) => this.handleScanData(scanType,data));
    this.connection.on('StandardData',(scanType: ScanTypes, data:any) => this.events.publish(ClaEvents.SCANDATA_EMG_STANDARD, data));
    this.connection.on('AlgoScanComplete', (data: any) => {this.events.publish(ClaEvents.SCANDATA_ALGO_COMPLETE, data);});
    this.connection.on('HrvScanComplete', (data: any) => {this.events.publish(ClaEvents.SCANDATA_HRV_COMPLETE, data);});
    this.connection.on('PowerLevel', (level: number) => {this.events.publish(ClaEvents.POWER_LEVEL, level);});
    this.connection.on('RawData', (data:any) => {this.events.publish(ClaEvents.RAWDATA, data);});
    this.connection.on('ThermalSpeed', (data: any) => {this.events.publish(ClaEvents.SCANDATA_THERMAL_SPEED, data);});
    this.connection.on('EmgPositionChange', (position: any) => {this.events.publish(ClaEvents.SCANDATA_EMG_POSITION, position)});
    this.connection.on('FirmwareProgress',(data: any) => {this.events.publish(ClaEvents.FIRMWARE_PROGRESS, data);});
    this.connection.on('RollingScanComplete', (data: any) => this.handleThermalRollingComplete(data));
    this.connection.on('SegmentalScanComplete', (data: any) => this.handleThermalSegmentalComplete(data));
    this.connection.on('gearonboardfound', (mac) => this.gearOnboardFound(mac));
    this.connection.on('gearonboardstopped', () => this.gearOnboardStopped());
    //this.connection.on('EmgProcessData', (data: any) => this.emg.ProcessData(data));

    this.connection.onclose(async e => {
      if(!this.disableDisconnectEvent) {
        this.events.publish(ClaEvents.BOX_DISCONNECT);
      }
    });

    this.connection.onreconnecting((err) => {
      this.isReconnecting = true;
    });

    this.connection.onreconnected((id) => {
      this.isReconnecting = false;
    });

    this.connection.serverTimeoutInMilliseconds = 1000 * 60 * 10;

    try {
      this.isReconnecting = false;
      this.disableDisconnectEvent = false;
      await this.connection.start();
    }
    catch(err){
      this.connectAttempt++;
      this.connection = undefined;
      return false;
    }

    this.boxDeviceId = '';

    let deviceInfo = await this.getDeviceInfo();

    this.box.CurrentVersion = deviceInfo.currentVersion;
    this.box.EmgMode = deviceInfo.emgMode;
    this.box.EmgNotchFreq = deviceInfo.emgNotchFreq;

    return true;
  }

  public async connectForOnboarding(box: Box) : Promise<boolean> {

    if(this.onBoardConnection != undefined && this.onBoardConnection.state == HubConnectionState.Connected) {
      return true;
    }

    let url = `https://${box.DeviceId}.${WEB_URL}:5002/signalr`;
    if(this.offlineMode || box.SslFallback || this.iOS11Available) {
      url = `http://${box.IpAddress}:5001/signalr`;
    }

    if(this.onBoardConnection == undefined) {
      this.onBoardConnection = new HubConnectionBuilder()
        .withUrl(url , {transport: HttpTransportType.WebSockets, skipNegotiation: true})
        .withAutomaticReconnect([0, 2000, 2000, 2000, 4000, 4000, 30000])
        .configureLogging(LogLevel.Information)
        .build();
    } else {
      this.onBoardConnection.baseUrl = url;
    }

    this.onBoardConnection.on('onboardazureconnected', () => this.onboardAzureConnected());

    this.onBoardConnection.serverTimeoutInMilliseconds = 1000 * 60 * 10;

    this.onBoardConnection.onclose = (error) => {
    };

    try {
      await this.onBoardConnection.start();
      return true;
    }
    catch (err) {
      this.onBoardConnection = undefined;
      return false;
    }
  }

  public async completeOnboarding() {
    await this.onBoardConnection.stop().catch((err) => {
    });
    this.onBoardConnection = undefined;
  }
  //endregion

  //region SCANNING
  public async startThermalConnection(settings: ThermalSettings) {
    try {
      return await this.connection.invoke<boolean>('ThermalConnect', settings);
    } catch (e) {
      return false;
    }
  }
  public async startConnection(settings: ScanSettings) {
    try {
      return await this.connection.invoke<boolean>('Connect', settings);
    } catch (e) {
      return false;
    }
  }

  public async sendThermalCompleteRx() {
    try {
      return await this.connection.invoke<boolean>('ThermalCompleteRx');
    } catch(e) {
      return false;
    }
  }

  public async loadProtocol(protocol: any, scanType: ScanTypes) {
    try {
      return await this.connection.invoke<boolean>('LoadProtocol', protocol, scanType);
    } catch(e) {
      return false;
    }
  }
  public async startScan(settings: any) {
    try {
      this._scanningState = settings;
      return await this.connection.invoke<boolean>('StartScan', settings);
    } catch(e) {
      return false;
    }
  }
  public async hrvScanStart() {
    try {
      await this.connection.invoke('HrvScanStart');
      return true;
    } catch(e) {
      return false;
    }
  }
  public async loadThermalProtocol(protocol: string) {
    try {
      return await this.connection.invoke<boolean>('loadThermalProtocol',protocol);
    } catch(e) {
      return false;
    }
  }
  public startCalibration() {
    try {
      this.connection.invoke('CalibrateStart');
      return true;
    } catch(e) {
      return false;
    }

  }

  public endCalibration() {
    try {
      this.connection.invoke('CalibrateComplete');
      return true;
    } catch(e) {
      return false;
    }

  }

  public resetCalibration() {
    try {
      this.connection.invoke('CalibrateReset');
      return true;
    } catch(e) {
      return false;
    }
  }

  public startCheckCalibration() {
    this.connection.invoke('CheckCalibration');
  }

  public updateHrvFirmwareRequest(url: string) {
    this.connection.invoke('UpdateHrvFirmware', url)
  }

  public updateEmgFirmwareRequest(url: string) {
    this.connection.invoke('UpdateEmgFirmware', url)
  }

  public setSpineLocation(spineLocation: SpinalLocation)
  {
    this.connection.invoke('SetSpineLocation', spineLocation);
  }

  public setProgramStep(programStep: number)
  {
    this.connection.invoke('SetProgramStep', programStep);
  }

  public setThermalProgramStep(programStep: number)
  {
    this.connection.invoke('SetThermalProgramStep', programStep);
  }

  public setMaxScale(scale: number)
  {
    this.connection.invoke('SetMaxScale', scale);
  }

  public emitSound(sound: number) {
    this.connection.invoke('ThermalMakeSound', sound);
  }

  public async emitSequence(sounds: number[]) {
    for (let s of sounds) {
      await this.connection.invoke('ThermalMakeSound', s);
      await delay(100);
    }
  }

  public saveNeutral(value: number) {
    this.connection.invoke('SaveNeutral', value);
  }

  public setTest(test: number) {
    this.connection.invoke('SetTest', test);
  }

  public async completeThermalScan(settings: ThermalSettings) {
    try {
      this._scanningState = null;
      return await this.connection.invoke<boolean>('CompleteThermalScan', settings);
    } catch(e) {
      return false;
    }
  }
  public async completeScan(settings: ScanSettings) {
    try {
      this._scanningState = null;
      return await this.connection.invoke<boolean>('CompleteScan', settings);
    } catch(e) {
      return false;
    }
  }

  public requestPowerLevel(settings: ScanSettings) {
    this.connection.invoke('RequestPowerLevel', settings);
  }

  private handleCalibrationResult(result: boolean) {
    this.events.publish(ClaEvents.CALIBRATION_RESULT, result);
  }

  private handleThermalRollingComplete(data: any) {
    let rawData = data.data;
    let c1l = data.c1left;
    let c1r = data.c1right;
    let offsetl = data.offset.left || 1;
    let offsetr = data.offset.right || 1;
    let rawDataArray = ScanViewUtils.parseByteArray(rawData);
    this.events.publish(ClaEvents.SCANDATA_THERMAL_ROLLINGCOMPLETE, rawDataArray, c1l, c1r, offsetl, offsetr, rawData);
  }

  private handleThermalSegmentalComplete(data: any) {
    this.events.publish(ClaEvents.SCANDATA_THERMAL_SEGMENTALCOMPLETE, data);
  }

  private handleScanData(scanType: ScanTypes, data: any) {
    switch (scanType) {
      case ScanTypes.EMG:
        this.events.publish(ClaEvents.SCANDATA_EMG_RX, data);
        break;
      case ScanTypes.HRV:
        this.events.publish(ClaEvents.SCANDATA_HRV_RX, data);
        break;
      case ScanTypes.Thermal:
        this.events.publish(ClaEvents.SCANDATA_THERMAL_RX, data);
        break;
      case ScanTypes.ROM:
        this.events.publish(ClaEvents.SCANDATA_ROM_RX, data);
        break;
      case ScanTypes.Algo:
        this.events.publish(ClaEvents.SCANDATA_ALGO_RX, data);
        break;

    }
  }

  public async redoRolling() {
    await this.connection.invoke('RedoRollingC1').catch(
      ()=>{
      });
  }

  public getLogs() {
    return this.connection.invoke<Log[]>('GetLogs');
  }

  //endregion

  //region ONBOARDING BOX

  public onboardingGetWifiNetworks() {
    return this.onBoardConnection.invoke<Network[]>('GetWifiNetworks');
  }

  public onboardingAddProfile(ssid, password) {
    return this.onBoardConnection.invoke<WiFiConnectionStatus>('AddProfile', ssid, password);
  }

  public async onboardingFinish(data) {
    await this.onBoardConnection.send('FinishOnboarding', data);

    try{
      await this.onBoardConnection.stop();
    }catch (e) {
      //ignore
    }
  }

  public async onBoardDeviceInfo() {
    try{
      let result = await this.onBoardConnection.invoke<any>('GetDeviceId');

      this.box.CurrentVersion = result.currentVersion;
      this.box.EmgMode = result.emgMode;
      this.box.EmgNotchFreq = result.emgNotchFreq;
      this.box.ThermalV2 = result.thermalV2;

      return result;
    }
    catch (e) {
    }
  }

  public onBoardGetLogs() {
    return this.onBoardConnection.invoke<Log[]>('GetLogs');
  }

  public checkInternet() {
    try {
      return this.onBoardConnection.invoke<boolean>('IsInternet');
    }
    catch(e)
    {
    }
  }
  //endregion

  //region ONBOARDING GEAR

  public async onboardGear(type: string) {
    switch (type) {
      case ScanTypes[ScanTypes.EMG].toString():
        this.connection.invoke('OnboardEmg').catch(()=>{return false});
        return true;

      case ScanTypes[ScanTypes.Thermal].toString():
        this.connection.invoke('OnboardThermal').catch(()=>{return false});
        return true;

      case ScanTypes[ScanTypes.HRV].toString():
        this.connection.invoke('OnboardHrv').catch(()=>{return false});
        return true;

      case ScanTypes[ScanTypes.ROM].toString():
        this.connection.invoke('OnboardRom').catch(()=>{return false});
        return true;
    }
  }

  public async manuallyAddGear(type: ScanTypes, mac: string): Promise<boolean> {
    return await this.connection.invoke('ManuallyAddGear',type,mac);
  }

  private gearOnboardFound(mac: string) {
    this.events.publish('gearonboard', {mac});
  }

  private gearOnboardStopped() {
    this.events.publish('gearonboard:stopped');
  }

  //endregion

  //region SETTINGS
  public setRegion(region) {
    return this.connection.invoke<any>('SetRegion',region);
  }

  public async toggleEmgNotchFreq() {
    return this.connection.invoke<any>('ToggleEmgNotchFreq');
  }

  public async toggleEmgMode() {
    return this.connection.invoke<any>('ToggleEmgMode');
  }

  public async getCompleteRollingPacket()
  {
    return this.connection.invoke<any>('GetThermalRollingCompletePacket');
  }
  public async getCompleteSegmentalPacket()
  {
    return this.connection.invoke<any>('GetThermalSegmentalCompletePacket');
  }
  public async getCompleteHrvPacket()
  {
    return this.connection.invoke<any>('GetHrvCompletePacket');
  }
  public async getCompleteAlgoPacket()
  {
    return this.connection.invoke<any>('GetAlgoCompletePacket');
  }

  public async getDeviceInfo() {
    try{
      let result = await this.connection.invoke<any>('GetDeviceId');

      this.box.CurrentVersion = result.currentVersion;
      this.box.EmgMode = result.emgMode;
      this.box.EmgNotchFreq = result.emgNotchFreq;

      return result;
    }
    catch (e) {
      await this.connectToBox(this.box);
    }
  }

  public forceUpdate() {
    this.connection.invoke('ForceUpdate');
  }

  public checkForUpdate() {
    return this.connection.invoke<any>('CheckForUpdate');
  }

  public async resetBox() {
    await this.connection.send('ResetBox');

    try{
      await this.connection.stop();
    }catch (e) {
      //ignore
    }
  }

  private onboardAzureConnected() {
    this.events.publish(ClaEvents.ONBOARD_AZURE_SUCCESS);
  }

  public setEMGSettings(config: EMGConfig){
    this.connection.invoke('SetEmgSettings', config)
  }


  //endregion

}



// WEBPACK FOOTER //
// ./src/providers/api-box/api-box.ts